// Liquid glass — port of the kube.io "Liquid Glass in CSS and SVG" GlassElement
// technique (https://kube.io/blog/liquid-glass-css-svg/), adapted to vanilla JS.
//
// Why JS instead of a static SVG filter: the displacement map must be generated
// from the element's actual width/height/radius so the refraction band follows
// the element's rounded (or circular) edge. A static, stretched map produces a
// SQUARE refraction band; this produces a correctly rounded one.
//
// The map draws directional gradients over the whole element, then stamps a
// neutral grey rounded-rect (rx/ry = radius, inset by `depth`, blurred by
// `depth`) back into the centre — cancelling displacement everywhere except a
// band that hugs the rounded edge. For a circle (radius = size/2) that band is
// a ring, so the refraction is circular.

// Base values used for every liquid-glass element. Per-element data-lg-*
// attributes (e.g. data-lg-depth="12") override these. Edit here to change the
// default look everywhere at once. Size/radius are not here — they come from
// the element (--lens-width, then border-radius / measured size).
export const LIQUID_GLASS_DEFAULTS = {
  depth: 14,
  strength: 110,
  chromaticAberration: 2,
  blur: 6,
};

/**
 * The displacement map: R encodes horizontal bend, G vertical. Neutral grey
 * (#808080 = no displacement) everywhere except the rounded edge band.
 * Gradients are inset by ~radius so they begin/end mid-curve.
 */
export const getDisplacementMap = ({height, width, radius, depth}) =>
  "data:image/svg+xml;utf8," +
  encodeURIComponent(`<svg height="${height}" width="${width}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
    <style>.mix { mix-blend-mode: screen; }</style>
    <defs>
        <linearGradient id="Y" x1="0" x2="0" y1="${Math.ceil((radius / height) * 15)}%" y2="${Math.floor(100 - (radius / height) * 15)}%">
            <stop offset="0%" stop-color="#0F0" />
            <stop offset="100%" stop-color="#000" />
        </linearGradient>
        <linearGradient id="X" x1="${Math.ceil((radius / width) * 15)}%" x2="${Math.floor(100 - (radius / width) * 15)}%" y1="0" y2="0">
            <stop offset="0%" stop-color="#F00" />
            <stop offset="100%" stop-color="#000" />
        </linearGradient>
    </defs>
    <rect x="0" y="0" height="${height}" width="${width}" fill="#808080" />
    <g filter="blur(2px)">
      <rect x="0" y="0" height="${height}" width="${width}" fill="#000080" />
      <rect x="0" y="0" height="${height}" width="${width}" fill="url(#Y)" class="mix" />
      <rect x="0" y="0" height="${height}" width="${width}" fill="url(#X)" class="mix" />
      <rect x="${depth}" y="${depth}" height="${height - 2 * depth}" width="${width - 2 * depth}" fill="#808080" rx="${radius}" ry="${radius}" filter="blur(${depth}px)" />
    </g>
</svg>`);

/**
 * The full filter: three displacement passes at slightly different scales whose
 * R/G/B channels are recombined → chromatic aberration. Returns a data: URI with
 * the `#displace` fragment, ready for `backdrop-filter: url(...)`.
 * Set chromaticAberration to 0 to drop the fringe (the passes collapse to one).
 */
export const getDisplacementFilter = ({height, width, radius, depth, strength = 100, chromaticAberration = 0,}) =>
  "data:image/svg+xml;utf8," +
  encodeURIComponent(`<svg height="${height}" width="${width}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
    <defs>
        <filter id="displace" color-interpolation-filters="sRGB">
            <feImage x="0" y="0" height="${height}" width="${width}" href="${getDisplacementMap({height, width, radius, depth})}" result="displacementMap" />
            <feDisplacementMap transform-origin="center" in="SourceGraphic" in2="displacementMap" scale="${strength + chromaticAberration * 2}" xChannelSelector="R" yChannelSelector="G" />
            <feColorMatrix type="matrix" values="1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0" result="displacedR" />
            <feDisplacementMap in="SourceGraphic" in2="displacementMap" scale="${strength + chromaticAberration}" xChannelSelector="R" yChannelSelector="G" />
            <feColorMatrix type="matrix" values="0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0" result="displacedG" />
            <feDisplacementMap in="SourceGraphic" in2="displacementMap" scale="${strength}" xChannelSelector="R" yChannelSelector="G" />
            <feColorMatrix type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0" result="displacedB" />
            <feBlend in="displacedR" in2="displacedG" mode="screen"/>
            <feBlend in2="displacedB" mode="screen"/>
        </filter>
    </defs>
</svg>`) +
  "#displace";

/**
 * Apply the liquid-glass effect to an element by setting its backdrop-filter.
 * Size defaults to the element's own --lens-width custom property (falling back
 * to its bounding box), so it works even while the element is on a hidden screen.
 *
 * @param {HTMLElement} el
 * @param {Object} [opts]
 * @param {number} [opts.width]   px (default: --lens-width or measured width)
 * @param {number} [opts.height]  px (default: --lens-width or measured height)
 * @param {number} [opts.radius]  corner radius px (default: width/2 = circle)
 * @param {number} [opts.depth]   refraction band width px
 * @param {number} [opts.strength]            displacement scale
 * @param {number} [opts.chromaticAberration] colour-fringe spread
 * @param {number} [opts.blur]    frost amount px (split before/after the filter)
 */
export function applyLiquidGlass(el, opts = {}) {
  const cs = getComputedStyle(el);
  const lensVar = parseFloat(cs.getPropertyValue("--lens-width"));
  const rect = el.getBoundingClientRect();

  const width = opts.width ?? (lensVar || Math.round(rect.width));
  const height = opts.height ?? (lensVar || Math.round(rect.height));
  const radius = opts.radius ?? width / 2; // circle by default
  const depth = opts.depth ?? LIQUID_GLASS_DEFAULTS.depth;
  const strength = opts.strength ?? LIQUID_GLASS_DEFAULTS.strength;
  const chromaticAberration = opts.chromaticAberration ?? LIQUID_GLASS_DEFAULTS.chromaticAberration;
  const blur = opts.blur ?? LIQUID_GLASS_DEFAULTS.blur;

  const filter = getDisplacementFilter({height, width, radius, depth, strength, chromaticAberration});
  const value = `blur(${blur / 2}px) url('${filter}') blur(${blur}px) brightness(1.1) saturate(1.5)`;

  el.style.backdropFilter = value;
  el.style.webkitBackdropFilter = value;
}

// Numeric data-attributes read off an element, e.g. data-lg-depth="12".
const NUMERIC_OPTS = {
  lgWidth: "width",
  lgHeight: "height",
  lgRadius: "radius",
  lgDepth: "depth",
  lgStrength: "strength",
  lgChromatic: "chromaticAberration",
  lgBlur: "blur",
};

/**
 * Liquid glass instance, one per element. Mirrors PillButton: build them all
 * with LiquidGlass.initAll(root). Re-applies on resize so it's correct once the
 * element is laid out / its screen is shown. Tune live with .set({...}).
 */
export class LiquidGlass {
  constructor(el) {
    this.el = el;
    this.options = LiquidGlass.readOptions(el);
    this.observer = new ResizeObserver(() => this.apply());
    this.observer.observe(el);
    this.apply();
  }

  // Read options from data-lg-* attributes (only the ones present).
  static readOptions(el) {
    const options = {};
    for (const [dataKey, optKey] of Object.entries(NUMERIC_OPTS)) {
      const raw = el.dataset[dataKey];
      if (raw == null || raw === "") continue;
      const value = parseFloat(raw);
      if (Number.isFinite(value)) options[optKey] = value;
    }
    return options;
  }

  // Merge new options and re-apply. Use from the console / debug panel to tune.
  set(options = {}) {
    Object.assign(this.options, options);
    this.apply();
    return this;
  }

  apply() {
    applyLiquidGlass(this.el, this.options);
  }

  destroy() {
    this.observer.disconnect();
    this.el.style.backdropFilter = "";
    this.el.style.webkitBackdropFilter = "";
  }

  // Build a LiquidGlass for every [data-liquid-glass] elements
  static initAll() {
    return Array.from(document.querySelectorAll("[data-liquid-glass]")).map((el) => new LiquidGlass(el));
  }
}
